import React, { useState, useRef } from "react";
import type { CalendarOperation, ParsedEvent } from "../types";
import CheckIcon from "./icons/CheckIcon";
import RepeatIcon from "./icons/RepeatIcon";
import { formatRecurrence } from "../services/rrule-parser";
import { formatFileSize, getFileTypeIcon } from "../utils/fileUtils";

interface EventCardProps {
  operation: CalendarOperation;
  attachments?: File[];
  onAttachmentsChange?: (files: File[]) => void;
  disabled?: boolean;
}

const OperationBadge: React.FC<{ type: CalendarOperation["type"] }> = ({ type }) => {
  const styles = {
    CREATE: "bg-green-500/20 text-green-300 border-green-500",
    UPDATE: "bg-yellow-500/20 text-yellow-300 border-yellow-500",
    DELETE: "bg-red-500/20 text-red-300 border-red-500",
    RSVP: "bg-blue-500/20 text-blue-300 border-blue-500",
  };
  return (
    <span
      className={`absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded-full border ${styles[type]}`}
    >
      {type}
    </span>
  );
};

const EventCard: React.FC<EventCardProps> = ({
  operation,
  attachments = [],
  onAttachmentsChange,
  disabled = false,
}) => {
  const { type, event, originalEvent, responseStatus, conflictWarning } = operation;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_ATTACHMENTS = 5;
  const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onAttachmentsChange) return;
    setAttachmentError(null);
    const selectedFiles = Array.from(e.target.files || []);

    // Deduplicate: skip files already attached (matched by name + size + lastModified)
    const newFiles = selectedFiles.filter(
      (f) =>
        !attachments.some(
          (existing) =>
            existing.name === f.name &&
            existing.size === f.size &&
            existing.lastModified === f.lastModified
        )
    );

    if (newFiles.length === 0) {
      if (e.target) e.target.value = "";
      return;
    }

    if (attachments.length + newFiles.length > MAX_ATTACHMENTS) {
      setAttachmentError(`Maximum ${MAX_ATTACHMENTS} attachments allowed. No files were added.`);
      if (e.target) e.target.value = "";
      return;
    }

    for (const file of newFiles) {
      if (file.size > MAX_FILE_SIZE) {
        setAttachmentError(`${file.name} is too large (max 10MB). No files were added.`);
        if (e.target) e.target.value = "";
        return;
      }
    }

    const totalSize = [...attachments, ...newFiles].reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      setAttachmentError("Total attachment size would exceed 25MB. No files were added.");
      if (e.target) e.target.value = "";
      return;
    }

    onAttachmentsChange([...attachments, ...newFiles]);
    if (e.target) e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    if (!onAttachmentsChange) return;
    onAttachmentsChange(attachments.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  const formatDate = (dateTime: string, timeZone: string | undefined) => {
    // Check if it's an all-day event (YYYY-MM-DD format)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateTime)) {
      const date = new Date(dateTime + "T00:00:00"); // Treat as local midnight to avoid tz shift
      return date.toLocaleDateString("en-US", {
        dateStyle: "full",
        timeZone: "UTC", // Use UTC to prevent the date from shifting
      });
    }
    try {
      const date = new Date(dateTime);
      // By specifying the event's timeZone, we ensure the time is displayed
      // correctly for that zone, accounting for DST.
      const formattedDate = date.toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: timeZone,
      });

      // Add timezone name if provided
      return timeZone ? `${formattedDate} (${timeZone})` : formattedDate;
    } catch (e) {
      console.error("Error formatting date:", dateTime, e);
      // Fallback for invalid date strings from the AI
      return "Invalid date";
    }
  };

  const findMyStatus = (attendees: ParsedEvent["attendees"]) => {
    if (!attendees) return "Unknown";
    const self = attendees.find((a) => a.self);
    return self?.responseStatus || "needsAction"; // Default to needsAction
  };

  // For DELETE/RSVP operations, the source of truth is the original event.
  // Otherwise, it's the new/updated event data.
  const displayEvent =
    (type === "DELETE" || type === "RSVP") && originalEvent ? originalEvent : event;
  const isDelete = type === "DELETE";

  const cardClasses = `relative bg-navy-deep/50 border border-border-strong rounded-sm p-4 shadow-md transition-all hover:border-rose-accent ${isDelete ? "opacity-70" : ""}`;
  const textClasses = isDelete ? "line-through text-text-muted" : "";

  return (
    <div className={cardClasses}>
      <OperationBadge type={type} />
      <h4
        className={`font-bold text-lg pr-20 ${isDelete ? "line-through text-red-400/70" : "text-rose-accent"}`}
      >
        {displayEvent.summary}
      </h4>

      {conflictWarning && (
        <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-300">
          <span className="flex-shrink-0 mt-0.5">&#x26A0;</span>
          <span>{conflictWarning}</span>
        </div>
      )}

      {!isDelete && displayEvent.recurrence && (
        <div className="mt-2 flex items-center gap-2 text-sm text-rose-bright">
          <RepeatIcon className="h-4 w-4 flex-shrink-0" />
          <span>{formatRecurrence(displayEvent.recurrence)}</span>
        </div>
      )}

      <div className={`mt-2 text-sm text-text-primary space-y-2 ${textClasses}`}>
        {type === "UPDATE" && originalEvent ? (
          <>
            {/* Time Diffing Logic */}
            <p>
              <strong>From:</strong>
              {event.start.dateTime !== originalEvent.start.dateTime ? (
                <span className="flex flex-wrap items-center gap-x-2">
                  <span className="line-through text-text-muted">
                    {formatDate(originalEvent.start.dateTime, originalEvent.start.timeZone)}
                  </span>
                  <span className="text-yellow-400 font-bold">→</span>
                  <span className="font-semibold">
                    {formatDate(event.start.dateTime, event.start.timeZone)}
                  </span>
                </span>
              ) : (
                formatDate(event.start.dateTime, event.start.timeZone)
              )}
            </p>
            <p>
              <strong>To:</strong>
              {event.end.dateTime !== originalEvent.end.dateTime ? (
                <span className="flex flex-wrap items-center gap-x-2">
                  <span className="line-through text-text-muted">
                    {formatDate(originalEvent.end.dateTime, originalEvent.end.timeZone)}
                  </span>
                  <span className="text-yellow-400 font-bold">→</span>
                  <span className="font-semibold">
                    {formatDate(event.end.dateTime, event.end.timeZone)}
                  </span>
                </span>
              ) : (
                formatDate(event.end.dateTime, event.end.timeZone)
              )}
            </p>
          </>
        ) : (
          <>
            {/* Standard Time Logic */}
            <p>
              <strong>From:</strong>{" "}
              {formatDate(displayEvent.start.dateTime, displayEvent.start.timeZone)}
            </p>
            <p>
              <strong>To:</strong>{" "}
              {formatDate(displayEvent.end.dateTime, displayEvent.end.timeZone)}
            </p>
          </>
        )}

        {type === "RSVP" && originalEvent && responseStatus && (
          <p>
            <strong>My Status:</strong>
            <span className="flex flex-wrap items-center gap-x-2">
              <span className="line-through text-text-muted capitalize">
                {findMyStatus(originalEvent.attendees)}
              </span>
              <span className="text-yellow-400 font-bold">→</span>
              <span className="font-semibold capitalize text-link-blue">{responseStatus}</span>
            </span>
          </p>
        )}

        {!isDelete && (
          <>
            {event.location && (
              <p>
                <strong>Location:</strong> {event.location}
              </p>
            )}

            {event.description && (
              <div
                className="text-text-secondary italic [&_a]:text-rose-accent [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
            )}

            {event.attendees && event.attendees.length > 0 && (
              <div className="pt-1">
                <p>
                  <strong>Attendees:</strong>
                </p>
                <ul className="list-disc list-inside ml-2">
                  {event.attendees.map((a) => (
                    <li key={a.email}>{a.displayName || a.email}</li>
                  ))}
                </ul>
              </div>
            )}

            {event.reminders?.overrides && event.reminders.overrides.length > 0 && (
              <div className="pt-1">
                <p>
                  <strong>Reminders:</strong>
                </p>
                <ul className="list-disc list-inside ml-2">
                  {event.reminders.overrides.map((r, i) => (
                    <li key={i}>
                      {r.minutes} minutes before ({r.method})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {event.reminders?.useDefault && (
              <div className="pt-1 flex items-center gap-2">
                <CheckIcon className="h-4 w-4 text-green-400" />
                <span>Using default calendar reminders.</span>
              </div>
            )}

            {type === "CREATE" && onAttachmentsChange && (
              <div className="pt-2 border-t border-border-strong mt-2">
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={disabled || attachments.length >= MAX_ATTACHMENTS}
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || attachments.length >= MAX_ATTACHMENTS}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border border-border-strong text-text-primary hover:bg-navy-mid/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                  Attach Files ({attachments.length}/{MAX_ATTACHMENTS})
                </button>

                {attachmentError && <p className="text-xs text-red-400 mt-1">{attachmentError}</p>}

                {attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {attachments.map((file, index) => {
                      const fileIcon = getFileTypeIcon(file.type);
                      return (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-2 py-1.5 bg-navy-darkest/50 rounded-sm"
                        >
                          <svg
                            className="w-4 h-4 flex-shrink-0"
                            viewBox={fileIcon.viewBox}
                            fill={fileIcon.color}
                          >
                            <path d={fileIcon.path} />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-text-primary truncate">{file.name}</p>
                            <p className="text-xs text-text-muted">{formatFileSize(file.size)}</p>
                          </div>
                          <button
                            onClick={() => removeAttachment(index)}
                            disabled={disabled}
                            className="p-0.5 rounded hover:bg-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg
                              className="w-3.5 h-3.5 text-text-secondary"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventCard;
